import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Input,
  Progress,
  Row,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import {
  BarChartOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  LinkOutlined,
  MessageOutlined,
  ReloadOutlined,
  SearchOutlined,
  StarFilled,
  StarOutlined,
  TableOutlined,
} from '@ant-design/icons';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as ReTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import dayjs from 'dayjs';

import { itAnalyticsAPI } from '../../api/client';
import DashboardCard from '../../components/dashboard/DashboardCard';
import MetricCard from '../../components/dashboard/MetricCard';
import FilterBar from '../../components/dashboard/FilterBar';
import ExportButton from '../../components/ExportButton';
import {
  makeTablePagination,
  normalizeTablePagination,
} from '../../utils/tablePagination';


const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

const DEFAULT_RANGE = () => [
  dayjs().subtract(3, 'month').startOf('month'),
  dayjs(),
];

const STATUS_LABELS = {
  __incoming__: 'Новые',
  'In progress': 'В работе',
  Review: 'На проверке',
  Estimate: 'Оценка трудозатрат',
  Waiting: 'Ожидание',
  'Security Approval': 'Согласование ИБ',
  Done: 'Выполнено',
  Rejected: 'Отклонено',
};

const STATUS_COLORS = {
  __incoming__: 'blue',
  'In progress': 'processing',
  Review: 'purple',
  Estimate: 'cyan',
  Waiting: 'default',
  'Security Approval': 'warning',
  Done: 'success',
  Rejected: 'error',
};


function count(value) {
  return Number(value || 0);
}


function decimal(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}


function percentageDelta(current, previous) {
  const currentValue = Number(current || 0);
  const previousValue = Number(previous || 0);

  if (previousValue === 0) {
    return currentValue === 0 ? '0%' : 'новое значение';
  }

  const delta = ((currentValue - previousValue) / previousValue) * 100;
  const sign = delta > 0 ? '+' : '';
  return `${sign}${delta.toFixed(1)}%`;
}


function numberDelta(current, previous, precision = 2) {
  if (current === null || current === undefined) {
    return 'нет данных';
  }
  if (previous === null || previous === undefined) {
    return 'нет данных за предыдущий период';
  }

  const delta = Number(current) - Number(previous);
  const sign = delta > 0 ? '+' : '';
  return `${sign}${delta.toFixed(precision)}`;
}


function formatPeriod(from, to) {
  if (!from || !to) return '—';
  return `${dayjs(from).format('DD.MM.YYYY')}–${dayjs(to).format('DD.MM.YYYY')}`;
}


function bucketRange(rawBucket, granularity) {
  const start = dayjs(rawBucket);
  let end = start.add(1, 'month').subtract(1, 'millisecond');
  let label = start.format('MMMM YYYY');

  if (granularity === 'hour') {
    end = start.add(1, 'hour').subtract(1, 'millisecond');
    label = `${start.format('DD.MM.YYYY HH:00')}–${end.format('HH:mm')}`;
  } else if (granularity === 'day') {
    end = start.add(1, 'day').subtract(1, 'millisecond');
    label = start.format('DD.MM.YYYY');
  } else if (granularity === 'week') {
    end = start.add(7, 'day').subtract(1, 'millisecond');
    label = `${start.format('DD.MM.YYYY')}–${end.format('DD.MM.YYYY')}`;
  }

  return { start, end, label };
}


export default function ITAnalyticsPage() {
  const [viewMode, setViewMode] = useState('charts');
  const [granularity, setGranularity] = useState('auto');
  const [dateRange, setDateRange] = useState(DEFAULT_RANGE());
  const [status, setStatus] = useState(null);
  const [assigneeGid, setAssigneeGid] = useState(null);

  const [requestState, setRequestState] = useState(null);
  const [requestStatus, setRequestStatus] = useState(null);
  const [requestRating, setRequestRating] = useState(null);
  const [requestSearch, setRequestSearch] = useState('');
  const [requestCreatedFrom, setRequestCreatedFrom] = useState(null);
  const [requestCreatedTo, setRequestCreatedTo] = useState(null);
  const [requestPeriodLabel, setRequestPeriodLabel] = useState(null);
  const [requestSort, setRequestSort] = useState({
    field: 'source_created_at',
    order: 'desc',
  });
  const [requestPagination, setRequestPagination] = useState({
    current: 1,
    pageSize: 25,
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
  });

  const params = {
    date_from: dateRange?.[0]?.format('YYYY-MM-DD'),
    date_to: dateRange?.[1]?.format('YYYY-MM-DD'),
    status: status || undefined,
    assignee_gid: assigneeGid || undefined,
    granularity,
  };

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['it-analytics', params],
    queryFn: () => (
      itAnalyticsAPI
        .getAnalytics(params)
        .then((response) => response.data)
    ),
  });

  const requestExportParams = {
    date_from: dateRange?.[0]?.format('YYYY-MM-DD'),
    date_to: dateRange?.[1]?.format('YYYY-MM-DD'),
    created_from: requestCreatedFrom || undefined,
    created_to: requestCreatedTo || undefined,
    state: requestState || undefined,
    status: requestStatus || undefined,
    assignee_gid: assigneeGid || undefined,
    rating: requestRating || undefined,
    search: requestSearch.trim() || undefined,
    sort_by: requestSort.field,
    sort_order: requestSort.order,
  };

  const requestParams = {
    ...requestExportParams,
    page: requestPagination.current,
    page_size: requestPagination.pageSize,
  };

  const {
    data: requestsData,
    isLoading: requestsLoading,
    isError: requestsError,
    error: requestsQueryError,
  } = useQuery({
    queryKey: ['it-analytics-requests', requestParams],
    queryFn: () => (
      itAnalyticsAPI
        .getRequests(requestParams)
        .then((response) => response.data)
    ),
    enabled: viewMode === 'requests',
  });

  const summary = data?.summary || {};

  const timelineData = (data?.timeline || []).map((row) => {
    const point = dayjs(row.bucket);

    let bucket = point.format('MMM YY');
    if (data?.granularity === 'hour') {
      bucket = point.format('DD.MM HH:mm');
    } else if (data?.granularity === 'day') {
      bucket = point.format('DD.MM');
    } else if (data?.granularity === 'week') {
      bucket = point.format('DD.MM.YY');
    }

    return {
      bucket,
      rawBucket: row.bucket,
      Заявки: count(row.requests),
      Выполнено: count(row.done),
    };
  });

  const statuses = (data?.statuses || []).map((row) => ({
    ...row,
    requests: count(row.requests),
    label: STATUS_LABELS[row.status] || row.status,
  }));

  /*
   * Для блока "Сейчас в работе" завершённые заявки не нужны:
   * иначе Done визуально уничтожает масштаб остальных состояний.
   */
  const activeStatuses = statuses.filter(
    (row) => !['Done', 'Rejected'].includes(row.status)
  );

  const maxActiveStatus = Math.max(
    1,
    ...activeStatuses.map((row) => row.requests)
  );

  const ratings = [5, 4, 3, 2, 1].map((value) => {
    const source = (data?.ratings || []).find(
      (row) => Number(row.rating) === value
    );

    return {
      rating: value,
      requests: count(source?.requests),
    };
  });

  const maxRatingCount = Math.max(
    1,
    ...ratings.map((row) => row.requests)
  );

  const assignees = useMemo(
    () => data?.assignees || [],
    [data]
  );

  const totalRequests = count(summary.total_requests);
  const openRequests = count(summary.open_requests);
  const doneRequests = count(summary.done_requests);
  const ratedRequests = count(summary.rated_requests);

  const openPercent = totalRequests
    ? Math.round((openRequests / totalRequests) * 100)
    : 0;

  const donePercent = totalRequests
    ? Math.round((doneRequests / totalRequests) * 100)
    : 0;

  const ratedPercent = totalRequests
    ? Math.round((ratedRequests / totalRequests) * 100)
    : 0;

  const comparison = data?.comparison || null;
  const previousSummary = comparison?.summary || {};
  const comparisonDescription = comparison
    ? (
      `Сравнение: предыдущий аналогичный период по дате создания · `
      + `текущий ${formatPeriod(
        comparison.current?.date_from,
        comparison.current?.date_to
      )} · предыдущий ${formatPeriod(
        comparison.previous?.date_from,
        comparison.previous?.date_to
      )}`
    )
    : null;

  const defaultRange = DEFAULT_RANGE();

  const hasFilters = Boolean(
    status
    || assigneeGid
    || dateRange?.[0]?.format('YYYY-MM-DD')
      !== defaultRange[0].format('YYYY-MM-DD')
    || dateRange?.[1]?.format('YYYY-MM-DD')
      !== defaultRange[1].format('YYYY-MM-DD')
  );

  const openRequestsView = ({
    state = null,
    status: targetStatus = null,
    rating = null,
    createdFrom = null,
    createdTo = null,
    periodLabel = null,
  } = {}) => {
    setRequestState(state);
    setRequestStatus(targetStatus);
    setRequestRating(rating);
    setRequestCreatedFrom(createdFrom);
    setRequestCreatedTo(createdTo);
    setRequestPeriodLabel(periodLabel);
    setRequestPagination((prev) => ({
      ...prev,
      current: 1,
    }));
    setViewMode('requests');
  };

  const handleTimelineClick = (chartState) => {
    const row = chartState?.activePayload?.[0]?.payload;
    if (!row?.rawBucket) return;

    const range = bucketRange(row.rawBucket, data?.granularity);
    openRequestsView({
      status: status || null,
      createdFrom: range.start.toISOString(),
      createdTo: range.end.toISOString(),
      periodLabel: range.label,
    });
  };

  const resetFilters = () => {
    setDateRange(DEFAULT_RANGE());
    setStatus(null);
    setAssigneeGid(null);
    setPagination((prev) => ({
      current: 1,
      pageSize: prev.pageSize,
    }));
  };

  const requestColumns = [
    {
      title: 'Заявка',
      dataIndex: 'request_number',
      key: 'request_number',
      sorter: true,
      sortOrder: (
        requestSort.field === 'request_number'
          ? (requestSort.order === 'asc' ? 'ascend' : 'descend')
          : null
      ),
      width: 115,
      render: (value, row) => (
        <a
          href={`https://app.asana.com/0/0/${row.asana_task_gid}/f`}
          target="_blank"
          rel="noreferrer"
        >
          <Space size={5}>
            {value}
            <LinkOutlined />
          </Space>
        </a>
      ),
    },
    {
      title: 'Создана',
      dataIndex: 'source_created_at',
      key: 'source_created_at',
      sorter: true,
      sortOrder: (
        requestSort.field === 'source_created_at'
          ? (requestSort.order === 'asc' ? 'ascend' : 'descend')
          : null
      ),
      width: 115,
      render: (value) => (
        value
          ? dayjs(value).format('DD.MM.YYYY')
          : '—'
      ),
    },
    {
      title: 'Статус',
      dataIndex: 'current_status',
      key: 'current_status',
      sorter: true,
      sortOrder: (
        requestSort.field === 'current_status'
          ? (requestSort.order === 'asc' ? 'ascend' : 'descend')
          : null
      ),
      width: 165,
      render: (value) => {
        const statusValue = value || '__incoming__';

        return (
          <Tag
            color={STATUS_COLORS[statusValue]}
            bordered={false}
          >
            {STATUS_LABELS[statusValue] || value || 'Новые'}
          </Tag>
        );
      },
    },
    {
      title: 'Исполнитель',
      dataIndex: 'current_assignee_name',
      key: 'current_assignee_name',
      sorter: true,
      sortOrder: (
        requestSort.field === 'current_assignee_name'
          ? (requestSort.order === 'asc' ? 'ascend' : 'descend')
          : null
      ),
      ellipsis: true,
      render: (value) => value || <Tag>Не назначено</Tag>,
    },
    {
      title: 'Заявитель',
      dataIndex: 'requester_email',
      key: 'requester_email',
      sorter: true,
      sortOrder: (
        requestSort.field === 'requester_email'
          ? (requestSort.order === 'asc' ? 'ascend' : 'descend')
          : null
      ),
      ellipsis: true,
      render: (value) => value || '—',
    },
    {
      title: 'Оценка',
      dataIndex: 'rating',
      key: 'rating',
      sorter: true,
      sortOrder: (
        requestSort.field === 'rating'
          ? (requestSort.order === 'asc' ? 'ascend' : 'descend')
          : null
      ),
      width: 95,
      align: 'right',
      render: (value) => (
        value
          ? (
            <Space size={4}>
              <StarFilled style={{ color: '#faad14' }} />
              {value}
            </Space>
          )
          : '—'
      ),
    },
  ];

  const columns = [
    {
      title: 'Исполнитель',
      dataIndex: 'assignee_name',
      key: 'assignee_name',
      sorter: (a, b) => (
        (a.assignee_name || '').localeCompare(
          b.assignee_name || '',
          'ru'
        )
      ),
      render: (value, row) => (
        row.assignee_gid
          ? value
          : <Tag>Не назначено</Tag>
      ),
    },
    {
      title: 'Всего',
      dataIndex: 'total_requests',
      key: 'total_requests',
      sorter: (a, b) => (
        count(a.total_requests) - count(b.total_requests)
      ),
      width: 85,
      align: 'right',
    },
    {
      title: 'Открыто',
      dataIndex: 'open_requests',
      key: 'open_requests',
      sorter: (a, b) => (
        count(a.open_requests) - count(b.open_requests)
      ),
      width: 90,
      align: 'right',
    },
    {
      title: 'Выполнено',
      dataIndex: 'done_requests',
      key: 'done_requests',
      sorter: (a, b) => (
        count(a.done_requests) - count(b.done_requests)
      ),
      width: 105,
      align: 'right',
    },
    {
      title: 'Оценено',
      dataIndex: 'rated_requests',
      key: 'rated_requests',
      sorter: (a, b) => (
        count(a.rated_requests) - count(b.rated_requests)
      ),
      width: 95,
      align: 'right',
    },
    {
      title: 'Оценка',
      dataIndex: 'average_rating',
      key: 'average_rating',
      sorter: (a, b) => (
        decimal(a.average_rating) - decimal(b.average_rating)
      ),
      width: 105,
      align: 'right',
      render: (value) => (
        value === null || value === undefined
          ? '—'
          : (
            <Space size={4}>
              <StarFilled style={{ color: '#faad14' }} />
              {Number(value).toFixed(2)}
            </Space>
          )
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          marginBottom: 18,
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <Title
            level={2}
            style={{
              margin: 0,
              fontWeight: 650,
              letterSpacing: '-0.02em',
            }}
          >
            IT Аналитика
          </Title>

          <Text type="secondary">
            Обращения и нагрузка IT Service Desk
          </Text>
        </div>

        <Button
          icon={<ReloadOutlined />}
          onClick={() => refetch()}
          loading={isLoading}
        >
          Обновить
        </Button>
      </div>

      <FilterBar>
        <Space
          wrap
          size={8}
          style={{
            width: '100%',
            justifyContent: 'space-between',
          }}
        >
          <Space wrap size={8}>
            <RangePicker
              value={dateRange}
              format="DD.MM.YYYY"
              onChange={(value) => {
                setDateRange(value);
                setPagination((prev) => ({
                  ...prev,
                  current: 1,
                }));
              }}
              presets={[
                {
                  label: '30 дней',
                  value: [
                    dayjs().subtract(30, 'day'),
                    dayjs(),
                  ],
                },
                {
                  label: '3 месяца',
                  value: [
                    dayjs().subtract(3, 'month'),
                    dayjs(),
                  ],
                },
                {
                  label: '6 месяцев',
                  value: [
                    dayjs().subtract(6, 'month'),
                    dayjs(),
                  ],
                },
              ]}
            />

            <Select
              placeholder="Статус"
              allowClear
              style={{ width: 190 }}
              value={status}
              onChange={(value) => {
                setStatus(value || null);
                setPagination((prev) => ({
                  ...prev,
                  current: 1,
                }));
              }}
              options={[
                { value: '__incoming__', label: 'Новые' },
                { value: 'In progress', label: 'В работе' },
                { value: 'Review', label: 'На проверке' },
                {
                  value: 'Estimate',
                  label: 'Оценка трудозатрат',
                },
                { value: 'Waiting', label: 'Ожидание' },
                {
                  value: 'Security Approval',
                  label: 'Согласование ИБ',
                },
                { value: 'Done', label: 'Выполнено' },
                { value: 'Rejected', label: 'Отклонено' },
              ]}
            />

            <Select
              placeholder="Исполнитель"
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: 220 }}
              value={assigneeGid}
              onChange={(value) => {
                setAssigneeGid(value || null);
                setPagination((prev) => ({
                  ...prev,
                  current: 1,
                }));
              }}
              options={[
                {
                  value: '__unassigned__',
                  label: 'Не назначено',
                },
                ...(data?.filter_options?.assignees || []).map(
                  (row) => ({
                    value: row.gid,
                    label: row.name,
                  })
                ),
              ]}
            />

            {hasFilters && (
              <Button
                type="text"
                onClick={resetFilters}
              >
                Сбросить
              </Button>
            )}
          </Space>

          <Segmented
            value={viewMode}
            onChange={setViewMode}
            options={[
              {
                label: 'Обзор',
                value: 'charts',
                icon: <BarChartOutlined />,
              },
              {
                label: 'Заявки',
                value: 'requests',
                icon: <SearchOutlined />,
              },
              {
                label: 'Команда',
                value: 'table',
                icon: <TableOutlined />,
              },
            ]}
          />
        </Space>
      </FilterBar>

      {isError && (
        <Alert
          type="error"
          showIcon
          message="Ошибка загрузки"
          description={
            error?.response?.data?.detail
            || 'Не удалось загрузить аналитику'
          }
          style={{ marginBottom: 16 }}
        />
      )}

      {comparisonDescription && (
        <Text
          type="secondary"
          style={{
            display: 'block',
            margin: '-2px 0 10px',
            fontSize: 12,
          }}
        >
          {comparisonDescription}
        </Text>
      )}

      <Row gutter={[14, 14]} style={{ marginBottom: 14 }}>
        <Col xs={24} sm={12} xl={6}>
          <MetricCard
            label="Всего заявок"
            value={totalRequests}
            hint="За выбранный период · открыть список"
            comparison={comparison ? (
              `${percentageDelta(
                totalRequests,
                previousSummary.total_requests
              )} к предыдущему периоду`
            ) : null}
            icon={<MessageOutlined />}
            onClick={() => openRequestsView()}
          />
        </Col>

        <Col xs={24} sm={12} xl={6}>
          <MetricCard
            label="Открыто"
            value={openRequests}
            hint={`${openPercent}% от всех заявок · открыть список`}
            comparison={comparison ? (
              `${percentageDelta(
                openRequests,
                previousSummary.open_requests
              )} к предыдущему периоду`
            ) : null}
            icon={<ClockCircleOutlined />}
            onClick={() => openRequestsView({ state: 'open' })}
          />
        </Col>

        <Col xs={24} sm={12} xl={6}>
          <MetricCard
            label="Выполнено"
            value={doneRequests}
            hint={`${donePercent}% от всех заявок · открыть список`}
            comparison={comparison ? (
              `${percentageDelta(
                doneRequests,
                previousSummary.done_requests
              )} к предыдущему периоду`
            ) : null}
            icon={<CheckCircleOutlined />}
            onClick={() => openRequestsView({ state: 'done' })}
          />
        </Col>

        <Col xs={24} sm={12} xl={6}>
          <MetricCard
            label="Средняя оценка"
            value={decimal(summary.average_rating).toFixed(2)}
            suffix="/ 5"
            hint={`Оценено ${ratedRequests} · ${ratedPercent}%`}
            comparison={comparison ? (
              `${numberDelta(
                summary.average_rating,
                previousSummary.average_rating
              )} к предыдущему периоду`
            ) : null}
            icon={<StarOutlined />}
          />
        </Col>
      </Row>

      <DashboardCard
        title="Активные заявки"
        style={{ marginBottom: 14 }}
        bodyStyle={{ padding: 14 }}
      >
        <Space wrap size={[8, 8]}>
          {activeStatuses.map((row) => (
            <Tag
              key={row.status}
              color={STATUS_COLORS[row.status]}
              bordered={false}
              style={{
                margin: 0,
                padding: '5px 9px',
                cursor: 'pointer',
                borderRadius: 7,
              }}
              onClick={() => openRequestsView({
                status: row.status,
              })}
            >
              {row.label}: {row.requests}
            </Tag>
          ))}

          {(() => {
            const unassigned = assignees.find(
              (row) => !row.assignee_gid
            );

            if (!unassigned?.open_requests) {
              return null;
            }

            return (
              <Tag
                bordered={false}
                color="red"
                style={{
                  margin: 0,
                  padding: '5px 9px',
                  cursor: 'pointer',
                  borderRadius: 7,
                }}
                onClick={() => {
                  setAssigneeGid('__unassigned__');
                  openRequestsView({ state: 'open' });
                }}
              >
                Без исполнителя: {unassigned.open_requests}
              </Tag>
            );
          })()}
        </Space>
      </DashboardCard>

      {viewMode === 'charts' ? (
        <>
          <Row gutter={[14, 14]} style={{ marginBottom: 14 }}>
            <Col xs={24} xl={16}>
              <DashboardCard
                title="Динамика обращений"
                extra={
                  <Segmented
                    size="small"
                    value={granularity}
                    onChange={setGranularity}
                    options={[
                      { label: 'Авто', value: 'auto' },
                      { label: 'Час', value: 'hour' },
                      { label: 'День', value: 'day' },
                      { label: 'Неделя', value: 'week' },
                      { label: 'Месяц', value: 'month' },
                    ]}
                  />
                }
                style={{ height: '100%' }}
              >
                <div style={{ height: 286 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={timelineData}
                      onClick={handleTimelineClick}
                      style={{ cursor: 'pointer' }}
                      margin={{
                        top: 8,
                        right: 8,
                        bottom: 0,
                        left: -10,
                      }}
                    >
                      <defs>
                        <linearGradient
                          id="requestsGradient"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor="#722ed1"
                            stopOpacity={0.16}
                          />
                          <stop
                            offset="95%"
                            stopColor="#722ed1"
                            stopOpacity={0}
                          />
                        </linearGradient>
                      </defs>

                      <CartesianGrid
                        stroke="#f0f0f0"
                        vertical={false}
                      />

                      <XAxis
                        dataKey="bucket"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#8c8c8c', fontSize: 12 }}
                      />

                      <YAxis
                        allowDecimals={false}
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#8c8c8c', fontSize: 12 }}
                      />

                      <ReTooltip />

                      <Area
                        type="monotone"
                        dataKey="Заявки"
                        stroke="#722ed1"
                        strokeWidth={2.2}
                        fill="url(#requestsGradient)"
                      />

                      <Area
                        type="monotone"
                        dataKey="Выполнено"
                        stroke="#52c41a"
                        strokeWidth={2}
                        fill="transparent"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </DashboardCard>
            </Col>

            <Col xs={24} xl={8}>
              <DashboardCard
                title="Сейчас в работе"
                style={{ height: '100%' }}
              >
                <Space
                  direction="vertical"
                  size={13}
                  style={{ width: '100%' }}
                >
                  {activeStatuses.length === 0 ? (
                    <Text type="secondary">
                      Нет открытых заявок
                    </Text>
                  ) : (
                    activeStatuses.map((row) => (
                      <div
                        key={row.status}
                        role="button"
                        tabIndex={0}
                        onClick={() => openRequestsView({
                          status: row.status,
                        })}
                        onKeyDown={(event) => {
                          if (
                            event.key === 'Enter'
                            || event.key === ' '
                          ) {
                            event.preventDefault();
                            openRequestsView({
                              status: row.status,
                            });
                          }
                        }}
                        style={{
                          cursor: 'pointer',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 8,
                            marginBottom: 5,
                          }}
                        >
                          <Tag
                            color={STATUS_COLORS[row.status]}
                            bordered={false}
                            style={{
                              margin: 0,
                              borderRadius: 6,
                            }}
                          >
                            {row.label}
                          </Tag>

                          <Text strong>
                            {row.requests}
                          </Text>
                        </div>

                        <Progress
                          percent={Math.round(
                            (row.requests / maxActiveStatus) * 100
                          )}
                          showInfo={false}
                          size="small"
                          strokeColor="#722ed1"
                          trailColor="#f2f2f2"
                        />
                      </div>
                    ))
                  )}
                </Space>
              </DashboardCard>
            </Col>
          </Row>

          <Row gutter={[14, 14]}>
            <Col xs={24} xl={8}>
              <DashboardCard
                title="Оценки пользователей"
                style={{ height: '100%' }}
              >
                <Space
                  direction="vertical"
                  size={14}
                  style={{ width: '100%' }}
                >
                  {ratings.map((row) => (
                    <div
                      key={row.rating}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '54px minmax(80px, 1fr) 42px',
                        alignItems: 'center',
                        gap: 10,
                      }}
                    >
                      <Space size={4}>
                        <Text strong>
                          {row.rating}
                        </Text>

                        <StarFilled
                          style={{ color: '#faad14' }}
                        />
                      </Space>

                      <Progress
                        percent={Math.round(
                          (row.requests / maxRatingCount) * 100
                        )}
                        showInfo={false}
                        strokeColor="#faad14"
                        trailColor="#f2f2f2"
                        size="small"
                      />

                      <Text
                        strong
                        style={{ textAlign: 'right' }}
                      >
                        {row.requests}
                      </Text>
                    </div>
                  ))}
                </Space>
              </DashboardCard>
            </Col>

            <Col xs={24} xl={16}>
              <DashboardCard title="Нагрузка команды">
                <Table
                  dataSource={assignees.slice(0, 8)}
                  columns={columns}
                  rowKey={(row) => (
                    row.assignee_gid || '__unassigned__'
                  )}
                  size="small"
                  pagination={false}
                  loading={isLoading}
                  scroll={{ x: 700 }}
                />
              </DashboardCard>
            </Col>
          </Row>
        </>
      ) : viewMode === 'requests' ? (
        <DashboardCard
          title="Заявки"
          extra={
            <Space size={10}>
              <Text type="secondary">
                Найдено: {requestsData?.total || 0}
              </Text>
              <ExportButton
                filenameBase="it-analytics-requests"
                onExport={(fileFormat) => (
                  itAnalyticsAPI.exportRequests(
                    requestExportParams,
                    fileFormat
                  )
                )}
              />
            </Space>
          }
        >
          <Space
            wrap
            size={8}
            style={{ marginBottom: 14 }}
          >
            {requestPeriodLabel && (
              <Tag
                color="purple"
                closable
                onClose={() => {
                  setRequestCreatedFrom(null);
                  setRequestCreatedTo(null);
                  setRequestPeriodLabel(null);
                  setRequestPagination((prev) => ({
                    ...prev,
                    current: 1,
                  }));
                }}
                style={{
                  padding: '4px 8px',
                  margin: 0,
                }}
              >
                Период графика: {requestPeriodLabel}
              </Tag>
            )}

            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Номер, текст, заявитель..."
              value={requestSearch}
              onChange={(event) => {
                setRequestSearch(event.target.value);
                setRequestPagination((prev) => ({
                  ...prev,
                  current: 1,
                }));
              }}
              style={{ width: 280 }}
            />

            <Select
              allowClear
              placeholder="Состояние"
              value={requestState}
              onChange={(value) => {
                setRequestState(value || null);
                setRequestStatus(null);
                setRequestPagination((prev) => ({
                  ...prev,
                  current: 1,
                }));
              }}
              style={{ width: 150 }}
              options={[
                { value: 'open', label: 'Открытые' },
                { value: 'done', label: 'Выполненные' },
                { value: 'rejected', label: 'Отклонённые' },
              ]}
            />

            <Select
              allowClear
              placeholder="Статус"
              value={requestStatus}
              onChange={(value) => {
                setRequestStatus(value || null);
                setRequestState(null);
                setRequestPagination((prev) => ({
                  ...prev,
                  current: 1,
                }));
              }}
              style={{ width: 190 }}
              options={Object.entries(STATUS_LABELS).map(
                ([value, label]) => ({
                  value,
                  label,
                })
              )}
            />

            <Select
              allowClear
              placeholder="Оценка"
              value={requestRating}
              onChange={(value) => {
                setRequestRating(value || null);
                setRequestPagination((prev) => ({
                  ...prev,
                  current: 1,
                }));
              }}
              style={{ width: 120 }}
              options={[5, 4, 3, 2, 1].map((value) => ({
                value,
                label: `${value} ★`,
              }))}
            />

            {(requestState
              || requestStatus
              || requestRating
              || requestSearch
              || requestPeriodLabel) && (
              <Button
                type="text"
                onClick={() => {
                  setRequestState(null);
                  setRequestStatus(null);
                  setRequestRating(null);
                  setRequestSearch('');
                  setRequestCreatedFrom(null);
                  setRequestCreatedTo(null);
                  setRequestPeriodLabel(null);
                  setRequestPagination((prev) => ({
                    ...prev,
                    current: 1,
                  }));
                }}
              >
                Очистить
              </Button>
            )}
          </Space>

          {requestsError && (
            <Alert
              type="error"
              showIcon
              message="Не удалось загрузить список заявок"
              description={
                requestsQueryError?.response?.data?.detail
                || 'Ошибка получения данных'
              }
              style={{ marginBottom: 14 }}
            />
          )}

          <Table
            dataSource={requestsData?.items || []}
            columns={requestColumns}
            rowKey="request_number"
            size="small"
            loading={requestsLoading}
            scroll={{ x: 950 }}
            expandable={{
              expandedRowRender: (row) => (
                <div
                  style={{
                    display: 'grid',
                    gap: 12,
                    padding: '4px 8px',
                  }}
                >
                  <div>
                    <Text strong>
                      Текст обращения
                    </Text>

                    <div
                      style={{
                        marginTop: 4,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {row.request_text || '—'}
                    </div>
                  </div>

                  {row.rating_comment && (
                    <div>
                      <Text strong>
                        Комментарий к оценке
                      </Text>

                      <div
                        style={{
                          marginTop: 4,
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {row.rating_comment}
                      </div>
                    </div>
                  )}

                  <div>
                    <a
                      href={`https://app.asana.com/0/0/${row.asana_task_gid}/f`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Space size={5}>
                        Открыть задачу в Asana
                        <LinkOutlined />
                      </Space>
                    </a>
                  </div>
                </div>
              ),
            }}
            pagination={{
              current: requestPagination.current,
              pageSize: requestPagination.pageSize,
              total: requestsData?.total || 0,
              showSizeChanger: true,
              pageSizeOptions: [10, 25, 50, 100],
              showTotal: (total) => `Всего ${total} заявок`,
            }}
            onChange={(page, filters, sorter) => {
              setRequestPagination({
                current: page.current || 1,
                pageSize: page.pageSize || 25,
              });

              if (sorter?.field || sorter?.columnKey) {
                setRequestSort({
                  field: sorter.field || sorter.columnKey,
                  order: sorter.order === 'ascend' ? 'asc' : 'desc',
                });
              }
            }}
          />
        </DashboardCard>
      ) : (
        <DashboardCard title="Команда">
          <Table
            dataSource={assignees}
            columns={columns}
            rowKey={(row) => (
              row.assignee_gid || '__unassigned__'
            )}
            size="small"
            loading={isLoading}
            scroll={{ x: 700 }}
            pagination={makeTablePagination({
              ...pagination,
              total: assignees.length,
              showTotal: (total) => (
                `Всего ${total} исполнителей`
              ),
            })}
            onChange={(page) => {
              setPagination(
                normalizeTablePagination(page)
              );
            }}
          />
        </DashboardCard>
      )}
    </div>
  );
}
